/**
 * Feature 073 — Documents sub-tab on the public SP profile.
 *
 * Renders the SP's attached PDFs with two actions:
 *   • View      → opens the PDF in a new browser tab
 *   • Download  → triggers a direct download
 *
 * Both actions target backend redirect endpoints
 * (`/providers/:providerId/documents/:docId/{view,download}`) which atomically
 * increment per-PDF counters and then 302 to the S3 URL. Counters are not
 * surfaced to public visitors.
 */

import { FileText, ExternalLink, Download } from 'lucide-react';
import { env } from '@/core/config/env';
import type { ProviderDocument } from '../types/provider-profile.types';

type ProviderDetailsDocumentsProps = {
  documents: ProviderDocument[];
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * The backend returns redirect paths like `/api/v1/providers/.../view`. The
 * browser would resolve those against the current document origin (the FE dev
 * server), which has no such route. Combine with the API host origin instead.
 */
const toAbsoluteUrl = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path;
  try {
    return new URL(path, env.API_BASE_URL).toString();
  } catch {
    return path;
  }
};

export const ProviderDetailsDocuments = ({
  documents,
}: ProviderDetailsDocumentsProps) => {
  if (documents.length === 0) {
    return null;
  }

  return (
    <ul className="space-y-3" aria-label="Provider documents">
      {documents.map((doc) => (
        <li
          key={doc.id}
          data-testid={`provider-document-${doc.id}`}
          className="flex items-center justify-between gap-4 rounded-md border border-border bg-card p-4"
        >
          <div className="flex min-w-0 items-start gap-3">
            <FileText
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground"
            />
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">
                {doc.displayName}
              </p>
              {doc.description ? (
                <p className="mt-0.5 text-sm text-muted-foreground">
                  {doc.description}
                </p>
              ) : null}
              <p className="mt-0.5 text-xs text-muted-foreground">
                PDF · {formatFileSize(doc.fileSize)} ·{' '}
                {new Date(doc.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <a
              href={toAbsoluteUrl(doc.viewUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
            >
              <ExternalLink aria-hidden="true" className="h-4 w-4" />
              View
            </a>
            <a
              href={toAbsoluteUrl(doc.downloadUrl)}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Download aria-hidden="true" className="h-4 w-4" />
              Download
            </a>
          </div>
        </li>
      ))}
    </ul>
  );
};
